import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Unstable_NumberInput as BaseNumberInput } from "@mui/base/Unstable_NumberInput";
import ColumnContainer from "../../../../common/ColumnContainer";
import ModalComponent from "../../../../common/ModalComponent";
import RowContainer from "../../../../common/RowContainer";
import { DashboardButton } from "../../../../common/DashboardButton";
import { AddRounded, RemoveRounded } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useUpdateSingleUserMutation } from "../../../../../redux/features/usersManagement/usersManagementApi";
import { useParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";

const CreditsCounter = ({
  count,
  currentUserCredits,
  handleChangeCounter,
  handleDecrement,
  handleIncrement,
}) => {
  return (
    <RowContainer center align="center" halfGap className="credits-counter">
      <IconButton onClick={handleDecrement} disabled={count === 0}>
        <RemoveRounded fontSize="small" />
      </IconButton>
      <input
        type="number"
        value={count}
        min={0}
        onChange={handleChangeCounter}
      />
      <IconButton onClick={handleIncrement}>
        <AddRounded fontSize="small" />
      </IconButton>
    </RowContainer>
  );
};

const CreditsModal = ({
  className,
  openModal,
  handleCloseModal,
  defaultCredits,
  currentUserCredits,
}) => {
  // INIT
  const { id: userId } = useParams();

  // EFFECTS

  // STATE
  const [count, setCount] = useState(0);
  const [reason, setReason] = useState("notSpecified");
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  // REDUX
  const [updateUser, { isLoading }] = useUpdateSingleUserMutation();

  // FUNCTIONS
  const handleDecrement = () => {
    if (count <= 0) {
      return;
    } else {
      setCount((prevCount) => prevCount - 1);
    }
  };

  const handleIncrement = () => {
    setCount((prevCount) => prevCount + 1);
  };

  const handleChangeCounter = (e) => {
    const value = parseInt(e.target.value);
    if (value === "" || isNaN(value)) {
      setCount(0);
    } else {
      setCount(value);
    }
  };

  const handleDefaultCreditsChoice = (e) => {
    const credit = e.target.innerText;
    setCount(parseInt(credit));
  };

  const handleResetCounter = () => {
    setCount(0);
  };

  const handleChangeReason = (event) => {
    setReason(event.target.value);
  };

  const handleAddCreditConfirm = async () => {
    const objectToSave = {
      credits: currentUserCredits + count,
      last_credits_update_reason: reason,
    };
    if (currentUserCredits + count > currentUserCredits) {
      try {
        const result = await updateUser({
          userId,
          ...objectToSave,
        }).unwrap();
        if (result) {
          handleCloseModal();
        }
      } catch (error) {
        console.error("Failed to update user:", error);
      }
      handleCloseModal();
      handleCloseConfirmModal();
    }
  };
  const handleOpenConfirmModal = () => {
    setOpenConfirmModal(true);
  };

  const handleCloseConfirmModal = () => {
    setOpenConfirmModal(false);
    setOpenConfirmModal(false);
  };

  return (
    <ModalComponent
      open={openModal}
      handleClose={handleCloseModal}
      className={className}
    >
      <ColumnContainer align="center" sx={{ gap: "3rem" }}>
        <RowContainer center>
          <Typography variant="h4" component="h4">
            <FormattedMessage id="users.addCredits.title" />
          </Typography>
        </RowContainer>
        <ColumnContainer align="center">
          <RowContainer center>
            {defaultCredits.map((credit) => (
              <DashboardButton
                type="filled"
                onClick={handleDefaultCreditsChoice}
              >
                <Typography variant="body1" component="p">
                  {credit}
                </Typography>
              </DashboardButton>
            ))}
          </RowContainer>
          <CreditsCounter
            handleDecrement={handleDecrement}
            handleChangeCounter={handleChangeCounter}
            handleIncrement={handleIncrement}
            count={count}
            currentUserCredits={currentUserCredits}
          ></CreditsCounter>
        </ColumnContainer>
        <RowContainer center sx={{ width: "200px" }}>
          <ColumnContainer minGap align="start">
            <Typography
              variant="p"
              component="p"
              fontSize="1rem"
              fontWeight="normal"
              color={"var(--main-color)"}
            >
              <FormattedMessage id="users.addCredits.reason" />
            </Typography>
            <Select
              sx={{
                width: "300px",
                fontSize: "16px",
              }}
              fullWidth
              value={reason || "notSpecified"}
              onChange={(event) => {
                handleChangeReason(event);
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: "auto",
                    backgroundColor: "var(--bkg-tertiary)",
                    "& .MuiMenuItem-root": {
                      fontSize: "16px",
                    },

                    "& .MuiMenuItem-root.Mui-selected": {
                      backgroundColor: "transparent",
                    },

                    "& .MuiMenuItem-root:hover": {
                      backgroundColor: "var(--bkg-tertiary)",
                    },
                  },
                },
              }}
            >
              <MenuItem value={"notSpecified"}>
                {" "}
                <FormattedMessage id="users.reason.notSpecified" />
              </MenuItem>
              <MenuItem value="bonus">
                <FormattedMessage id="users.reason.welcome" />
              </MenuItem>
              <MenuItem value="refund">
                <FormattedMessage id="users.reason.refund" />
              </MenuItem>
            </Select>
          </ColumnContainer>
        </RowContainer>
        <RowContainer align="center" end minGap sx={{ alignSelf: "end" }}>
          <DashboardButton type="outlined" onClick={handleResetCounter}>
            Reset
          </DashboardButton>
          <DashboardButton type="filled" onClick={handleOpenConfirmModal}>
            <FormattedMessage id="button.confirm" />
          </DashboardButton>
        </RowContainer>
      </ColumnContainer>
      <ConfirmModal
        openConfirmModal={openConfirmModal}
        handleCloseConfirmModal={handleCloseConfirmModal}
        addingCredits={count}
        reason={reason}
        currentUserCredits={currentUserCredits}
        handleAddCreditConfirm={handleAddCreditConfirm}
      ></ConfirmModal>
    </ModalComponent>
  );
};
export default CreditsModal;

const ConfirmModal = ({
  openConfirmModal,
  handleCloseConfirmModal,
  addingCredits,
  currentUserCredits,
  reason,
  handleAddCreditConfirm,
}) => {
  return (
    <ModalComponent
      open={openConfirmModal}
      handleClose={handleCloseConfirmModal}
      className={"confirm-credit-modal"}
    >
      <ColumnContainer center align="center" padding="30px">
        <ColumnContainer halfGap>
          <Typography
            variant="p"
            component="p"
            sx={{ color: "var(--text-primary)" }}
          >
            Sei sicuro di voler aggiungere {addingCredits} crediti all'utente,
            con motivazione "{reason}"?
          </Typography>
          <Typography
            variant="p"
            component="p"
            sx={{ color: "var(--text-primary)" }}
          >
            L'utente raggiungerà quota {currentUserCredits + addingCredits}{" "}
            crediti.
          </Typography>
        </ColumnContainer>
        <RowContainer align="center" end minGap sx={{ alignSelf: "end" }}>
          <DashboardButton type="outlined" onClick={handleCloseConfirmModal}>
            Indietro
          </DashboardButton>
          <DashboardButton type="filled" onClick={handleAddCreditConfirm}>
            Conferma
          </DashboardButton>
        </RowContainer>
      </ColumnContainer>
    </ModalComponent>
  );
};
